import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetQuoteCurationWorkspace,
  getGetQuoteCurationWorkspaceQueryKey,
  usePreviewQuoteCuration,
  useCurateQuote,
  useRetryQuoteCuration,
  type QuoteCurationInput,
  type QuoteCurationInputAction,
  type QuoteResolutionCandidate,
} from "@workspace/api-client-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { CuratorFields } from "./CuratorFields";
import { SurvivorPicker } from "./SurvivorPicker";
import { CurationRecordPanel } from "./CurationRecordPanel";
import {
  buildPatch, describeError, formFromQuote, humanize, invalidateAfterCuration, isOriginal, validateForm,
  type CuratedQuote, type CurationForm,
} from "./curationUtils";

const ACTIONS: { value: QuoteCurationInputAction; label: string }[] = [
  { value: "edit", label: "Correct" },
  { value: "merge", label: "Resolve duplicate" },
  { value: "remove", label: "Withdraw" },
];

function workState(d: unknown): string | undefined {
  const w = (d as { work?: { state?: unknown } } | undefined)?.work;
  return typeof w?.state === "string" ? w.state : undefined;
}

export function QuoteCurationDialog({ quoteId, open, onOpenChange, onRemoved }: {
  quoteId: number; open: boolean; onOpenChange: (o: boolean) => void; onRemoved?: (id: number) => void;
}) {
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState(quoteId);
  useEffect(() => { if (open) setActiveId(quoteId); }, [open, quoteId]);

  const ws = useGetQuoteCurationWorkspace(activeId, {
    query: {
      queryKey: getGetQuoteCurationWorkspaceQueryKey(activeId),
      enabled: open && activeId > 0,
      refetchInterval: (q) => {
        const st = workState(q.state.data);
        return st === "queued" || st === "running" ? 4000 : false;
      },
    },
  });
  const quote = ws.data?.quote as CuratedQuote | undefined;
  const serverRevision = ws.data?.revision ?? 0;
  const archived = !!(ws.data as { archived?: boolean } | undefined)?.archived;

  const [action, setAction] = useState<QuoteCurationInputAction>("edit");
  const [form, setForm] = useState<CurationForm | null>(null);
  const [reason, setReason] = useState("");
  const [survivor, setSurvivor] = useState<QuoteResolutionCandidate | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [closedMessage, setClosedMessage] = useState<string | null>(null);
  const initKey = useRef<string | null>(null);
  // Removals defer the broad list refresh until the desk closes, so the
  // confirmation is not torn down when the originating card leaves the list.
  const deferredRemoval = useRef<number | null | undefined>(undefined);
  const handleOpenChange = (o: boolean) => {
    if (!o && deferredRemoval.current !== undefined) {
      const id = deferredRemoval.current;
      deferredRemoval.current = undefined;
      invalidateAfterCuration(qc, id);
      if (id != null) onRemoved?.(id);
    }
    onOpenChange(o);
  };

  // The draft baseline is the snapshot taken when the draft was opened, never
  // the latest polled quote: background work may change status/flags/form
  // without bumping the manual revision, and untouched fields must not turn
  // into human overrides. The revision sent is the baseline's revision, so a
  // concurrent manual edit surfaces as an explicit 409 instead of a reset.
  const [base, setBase] = useState<CurationForm | null>(null);
  const [revision, setRevision] = useState(0);
  const dirty = !!(form && base && Object.keys(buildPatch(base, form)).length > 0);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    if (!quote) return;
    const key = `${quote.id}:${serverRevision}`;
    // Producer updates may finish at the same manual revision. Refresh a clean
    // form, but never rebase an unsaved human draft onto those producer values.
    if (initKey.current === key && dirtyRef.current) return;
    // A new manual revision arrived while editing: keep the draft and its baseline.
    if (initKey.current?.startsWith(`${quote.id}:`) && dirtyRef.current) return;
    initKey.current = key;
    const snap = formFromQuote(quote);
    setBase(snap); setForm(snap); setRevision(serverRevision);
  }, [quote, serverRevision]);
  const staleRevision = !!base && serverRevision !== revision;

  // When queued/running work completes, refresh other surfaces once.
  const prevState = useRef<string | undefined>(undefined);
  const curState = workState(ws.data);
  useEffect(() => {
    const prev = prevState.current;
    prevState.current = curState;
    if ((prev === "queued" || prev === "running") && curState === "completed") {
      invalidateAfterCuration(qc, null);
      setNotice("Follow-up work completed.");
    }
  }, [curState, qc]);
  useEffect(() => { prevState.current = undefined; }, [activeId]);

  useEffect(() => {
    if (!open) {
      initKey.current = null;
      setForm(null); setBase(null); setReason(""); setSurvivor(null); setAction("edit");
      setConfirmed(false); setError(null); setNotice(null); setClosedMessage(null);
    }
  }, [open]);

  const input: QuoteCurationInput | null = useMemo(() => {
    if (!form || !base) return null;
    const i: QuoteCurationInput = { revision, reason: reason.trim(), action };
    if (action === "edit") i.patch = buildPatch(base, form);
    if (action === "merge" && survivor) { i.survivorId = survivor.quote.id; i.survivorRevision = survivor.revision; }
    return i;
  }, [form, base, revision, reason, action, survivor]);
  const inputKey = JSON.stringify(input);

  const preview = usePreviewQuoteCuration();
  const curate = useCurateQuote();
  const retry = useRetryQuoteCuration();
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const previewFresh = preview.data && previewKey === inputKey;

  useEffect(() => { setConfirmed(false); }, [inputKey]);

  const formError = action === "edit" && form ? validateForm(form) : null;
  const noChanges = action === "edit" && input?.patch && Object.keys(input.patch).length === 0;
  const reasonOk = reason.trim().length >= 3 && reason.trim().length <= 1000;
  const blocker =
    (archived ? "This quote is archived and read-only." : null) ?? formError ?? (noChanges ? "Change at least one field." : null) ??
    (action === "merge" && !survivor ? "Select a surviving quote." : null) ??
    (!reasonOk ? "A reason of at least 3 characters is required." : null);

  function runPreview() {
    if (!input || blocker) return;
    setError(null); setNotice(null);
    const key = inputKey;
    preview.mutate({ id: activeId, data: input }, {
      onSuccess: () => setPreviewKey(key),
      onError: (e) => setError(describeError(e)),
    });
  }

  function commit() {
    if (!input || !previewFresh) return;
    setError(null);
    curate.mutate({ id: activeId, data: input }, {
      onSuccess: (res) => {
        const removedId = action === "edit" ? null : activeId;
        if (removedId == null) invalidateAfterCuration(qc, null);
        else {
          qc.removeQueries({ queryKey: [`/api/quotes/${removedId}`] });
          deferredRemoval.current = removedId;
        }
        setPreviewKey(null); setReason(""); setConfirmed(false); setSurvivor(null);
        if (res.quoteId == null) {
          setClosedMessage(res.message || "Quote withdrawn.");
        } else if (res.quoteId !== activeId) {
          initKey.current = null;
          setAction("edit");
          qc.invalidateQueries({ queryKey: getGetQuoteCurationWorkspaceQueryKey(res.quoteId) });
          setActiveId(res.quoteId);
          setNotice(`${res.message || "Duplicate resolved."} Now viewing survivor #${res.quoteId}.`);
        } else {
          initKey.current = null;
          setNotice(res.message || "Correction saved.");
          ws.refetch();
        }
      },
      onError: (e) => setError(describeError(e)),
    });
  }

  function runRetry() {
    retry.mutate({ id: activeId, data: { revision } }, {
      onSuccess: () => { qc.invalidateQueries({ queryKey: getGetQuoteCurationWorkspaceQueryKey(activeId) }); setNotice("Retry queued."); },
    });
  }

  const destructive = action !== "edit";
  const btn = "rounded-xl px-4 py-2 text-sm font-medium transition active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 disabled:opacity-50 disabled:active:scale-100";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-h-[92dvh] w-[calc(100vw-1.5rem)] max-w-5xl overflow-y-auto rounded-3xl border-stone-200 bg-[#fbfaf7] p-0 sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        data-testid="quote-curation-dialog"
      >
        <div className="border-b border-stone-200 px-5 pb-4 pt-5 sm:px-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700">Curator desk</p>
          <DialogTitle className="mt-1 text-xl font-semibold text-stone-900">Quote #{activeId}{ws.data ? ` · rev ${revision}` : ""}</DialogTitle>
          <DialogDescription className="text-sm text-stone-500">Correct the credited record. Ownership and model evidence stay untouched.</DialogDescription>
        </div>

        {closedMessage ? (
          <div className="px-6 py-12 text-center" role="status">
            <p className="font-serif text-2xl text-stone-900">Withdrawn.</p>
            <p className="mt-2 text-sm text-stone-500">{closedMessage}</p>
            <button type="button" className={`${btn} mt-6 bg-stone-900 text-white hover:bg-stone-700`} onClick={() => handleOpenChange(false)}>Close</button>
          </div>
        ) : ws.isLoading || (!form && !ws.isError) ? (
          <div className="grid gap-4 p-6 md:grid-cols-[1fr_20rem]">
            <div className="space-y-3">{[1, 2, 3, 4].map((i) => <div key={i} className="h-14 animate-pulse rounded-2xl bg-stone-100" />)}</div>
            <div className="h-64 animate-pulse rounded-2xl bg-stone-100" />
          </div>
        ) : ws.isError || !quote || !form || !ws.data ? (
          <div className="p-6" role="alert">
            <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{describeError(ws.error)}</p>
            <button type="button" className={`${btn} mt-4 border border-stone-200 text-stone-700 hover:bg-stone-100`} onClick={() => ws.refetch()}>Retry</button>
          </div>
        ) : (
          <div className="grid gap-6 p-5 sm:p-6 md:grid-cols-[minmax(0,1fr)_20rem]">
            <div className="min-w-0 space-y-5">
              {notice && <p role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{notice}</p>}

              <div role="radiogroup" aria-label="Curation action" className="inline-flex flex-wrap gap-1 rounded-2xl bg-stone-100 p-1">
                {ACTIONS.map((a) => (
                  <button key={a.value} type="button" role="radio" aria-checked={action === a.value} disabled={archived} onClick={() => { setAction(a.value); setError(null); }}
                    className={`rounded-xl px-3 py-1.5 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 ${action === a.value ? (a.value === "edit" ? "bg-white text-stone-900 shadow-sm" : "bg-white text-rose-700 shadow-sm") : "text-stone-500 hover:text-stone-800"}`}>
                    {a.label}
                  </button>
                ))}
              </div>

              {archived && <p role="status" className="rounded-xl border border-stone-300 bg-stone-100 px-3 py-2 text-sm text-stone-700">Archived quote. The record is read-only; audit history and failed-work retry remain available.</p>}
              {staleRevision && !archived && <p role="status" className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">Another curator saved revision {serverRevision} while you were editing (your draft is based on rev {revision}). Saving will return a conflict; close and reopen to start from the latest record.</p>}
              {!archived && action === "edit" && <CuratorFields form={form} setForm={setForm} original={{ isOriginal: isOriginal(quote), ownerName: isOriginal(quote) ? (quote.ownerUsername ?? null) : null }} />}
              {!archived && action === "merge" && <SurvivorPicker current={quote} survivor={survivor} onSelect={setSurvivor} />}
              {!archived && action === "remove" && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50/60 p-4 text-sm text-rose-900">
                  <p className="font-serif text-lg leading-snug">&ldquo;{quote.text}&rdquo;</p>
                  <p className="mt-1 text-xs text-rose-700">{quote.author}</p>
                  <p className="mt-3 text-xs leading-relaxed">Withdrawal deletes the active quote and its dependent data. An admin-only snapshot is kept. Members' saves and pins on this quote will be removed.</p>
                </div>
              )}

              {!archived && <div>
                <label htmlFor="cur-reason" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone-500">Reason (required)</label>
                <textarea id="cur-reason" rows={2} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Misattributed; verified against the 1859 edition"
                  className="w-full resize-y rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400" />
              </div>}

              {previewFresh && preview.data && (
                <section aria-label="Preview" className="space-y-2 rounded-2xl border border-sky-200 bg-sky-50/60 p-4 text-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-sky-800">Preview</p>
                  {preview.data.changedFields.length > 0 && <p className="text-stone-700"><span className="text-stone-500">Changes:</span> {preview.data.changedFields.map(humanize).join(", ")}</p>}
                  {preview.data.stages.length > 0 && <p className="text-stone-700"><span className="text-stone-500">Will run:</span> {preview.data.stages.map(humanize).join(", ")}</p>}
                  {Object.keys(preview.data.counts).length > 0 && (
                    <p className="text-stone-700"><span className="text-stone-500">Affected activity:</span> {Object.entries(preview.data.counts).map(([k, v]) => `${humanize(k)} ${v}`).join(" · ")}</p>
                  )}
                  {action === "merge" && <p className="text-xs text-stone-500">Saves deduplicate onto the survivor. Where a member voted on both, the survivor vote wins; otherwise the duplicate vote moves. Votes are never summed.</p>}
                  {preview.data.warnings.map((w) => <p key={w} className="rounded-lg bg-amber-50 px-2 py-1 text-xs text-amber-800">{w}</p>)}
                </section>
              )}

              {previewFresh && destructive && (
                <label className="flex cursor-pointer items-start gap-2 rounded-2xl border border-rose-200 bg-white p-3 text-sm text-rose-900">
                  <input type="checkbox" className="mt-0.5 h-4 w-4 accent-rose-700" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
                  {action === "merge" && survivor
                    ? `Remove #${activeId} and move compatible activity to survivor #${survivor.quote.id}.`
                    : `Permanently withdraw #${activeId} and remove its active references.`}
                </label>
              )}

              {(error || (blocker && reason.length > 0)) && (
                <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error ?? blocker}</p>
              )}

              <div className="flex flex-wrap items-center justify-end gap-2 border-t border-stone-200 pt-4">
                <button type="button" className={`${btn} text-stone-500 hover:bg-stone-100`} onClick={() => handleOpenChange(false)}>Cancel</button>
                <button type="button" className={`${btn} border border-stone-300 bg-white text-stone-800 hover:bg-stone-50`} disabled={!!blocker || preview.isPending} onClick={runPreview}>
                  {preview.isPending ? "Previewing" : previewFresh ? "Preview again" : "Preview"}
                </button>
                <button type="button"
                  className={`${btn} text-white ${destructive ? "bg-rose-700 hover:bg-rose-800" : "bg-stone-900 hover:bg-stone-700"}`}
                  disabled={archived || !previewFresh || curate.isPending || (destructive && !confirmed)}
                  title={!previewFresh ? "Preview the current changes first" : undefined}
                  onClick={commit}>
                  {curate.isPending ? "Saving" : action === "edit" ? "Save correction" : action === "merge" ? "Resolve duplicate" : "Withdraw quote"}
                </button>
              </div>
            </div>

            <aside className="min-w-0">
              <CurationRecordPanel ws={ws.data} onRetry={runRetry} retrying={retry.isPending} retryError={retry.isError ? describeError(retry.error) : null} />
            </aside>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
